import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Tooltip } from 'styles';
import { IconArrowLeft, IconListSearch } from '@tabler/icons-react';
import { formatPrice } from 'utils/price';
import { IconButton } from 'ui/Button';
import { Loading } from 'components/Loading';
import { OrderDetailsProducts } from './OrderDetailsProducts';
import { FormattedDeliveryDate } from 'pages/Totem/TotemDashboard/FormattedDeliveryDate';
import { calculateOrderPrices } from 'pages/Order/OrderDetails/utils/calculateOrderPrices';
import { getDiscountFromOrder } from 'pages/Order/utils/getDiscountFromOrder';
import { mergeProductsAndProductsRequested } from 'pages/Order/OrderDetails/utils/mergeProductsAndProductsRequested';
import { getDeliveryHourRangeString } from 'utils/time';
import { usePriceVisibility } from 'hooks/usePriceVisibility';
import { GetOrderQuery, useGetOrderQuery } from 'data/__generated__';

// GET_ORDER enrichit OrderFragment (productsWithQuantityPicked, productsRequestedButNotFullyDelivered).
type Order = GetOrderQuery['getOrder'];

interface GetOrderData {
    getOrder: Order;
}

const getFrequencyLabel = (frequency: string): string => {
    switch (frequency) {
        case 'Weekly':
            return 'Hebdomadaire';
        case 'BiWeekly':
            return 'Bi-mensuelle';
        case 'Monthly':
            return 'Mensuelle';
        case 'Quarterly':
            return 'Trimestrielle';
        case 'BiAnnually':
            return 'Semestrielle';
        default:
            return '';
    }
};

export const OrderDetails: React.FC = () => {
    const { orderId } = useParams();
    const navigate = useNavigate();

    const { loading, data: getOrderData } = useGetOrderQuery({
        variables: {
            orderId: orderId ?? '',
        },
    });
    const { keepVisiblePriceProducts } = usePriceVisibility();

    if (loading) {
        return <Loading />;
    }
    const { getOrder: order } = getOrderData as GetOrderData;

    const productsWithQuantityRequested = mergeProductsAndProductsRequested({
        products: order?.productsWithQuantityPicked ?? [],
        productRequestedWithQuantities: order?.productsRequestedButNotFullyDelivered ?? [],
        deliveryDate: order?.date_delivery ?? '',
        priceDelivery: order?.prices.priceDelivery ?? 0,
    });

    const productsToShow = productsWithQuantityRequested?.sort((productA, productB) => {
        const quantityA = typeof productA?.quantity === 'number' ? productA.quantity : 0;
        const quantityB = typeof productB?.quantity === 'number' ? productB.quantity : 0;
        const quantityPickedA = typeof productA?.quantityPicked === 'number' ? productA.quantityPicked : 0;
        const quantityPickedB = typeof productB?.quantityPicked === 'number' ? productB.quantityPicked : 0;
        const quantityNotDeliveredA = quantityPickedA ? quantityA - quantityPickedA : quantityA;
        const quantityNotDeliveredB = quantityPickedB ? quantityB - quantityPickedB : quantityB;
        if (quantityNotDeliveredA > quantityNotDeliveredB) {
            return -1;
        }
        if (quantityNotDeliveredA < quantityNotDeliveredB) {
            return 1;
        }
        return 0;
    });

    // Les produits dont le prix est masqué ne pèsent pas dans les montants AFFICHÉS — sinon le
    // sous-total d'une commande mono-produit rend le prix qu'on vient de masquer sur la ligne. Les
    // prix réels restent intacts côté commande, Odoo et facture ; `calculateOrderPrices` reste pur.
    const { allOrderedProductsPrice, allProductsPrice, allProductPriceWithDelivery } = calculateOrderPrices({
        products: keepVisiblePriceProducts(productsToShow),
        priceDelivery: order?.prices?.priceDelivery ?? 0,
    });

    const { discountPrice, formattedDiscountPrice, formattedDiscountEffect, discountDescription } =
        getDiscountFromOrder({
            order,
            totalHT: allProductPriceWithDelivery,
        });

    const totalPriceAfterDiscount = allProductPriceWithDelivery - discountPrice;

    return (
        <Container>
            <PageHeader>
                <IconButton
                    data-test="return-to-totem"
                    $rotateOnHover
                    $large
                    $white
                    onClick={() => navigate(`/totem/${order.totemId}`)}
                >
                    <IconArrowLeft size={25} />
                </IconButton>
            </PageHeader>
            <ScrollableSection>
                <PageTitleSection>
                    <IconListSearch size={21} />
                    <PageTitle>Détails de la commande</PageTitle>
                </PageTitleSection>
                <OrderDetailsContent>
                    <OrderDetailsHeader>
                        <OrderInfoSection>
                            <OrderTitle>
                                Commande du{' '}
                                <FormattedDeliveryDate deliveryDate={order.date_delivery} shouldCapitalize={false} />
                            </OrderTitle>
                            <OrderInfo>
                                <OrderInfoElement>
                                    {order.isPunctual ? (
                                        'Ponctuelle'
                                    ) : (
                                        <span>
                                            Récurrente{' '}
                                            {order.previous_orderId && order.previous_orderId !== 'firstOrder' && (
                                                <Link to={`/order/${order.previous_orderId}`}>
                                                    (voir la commande précédente)
                                                </Link>
                                            )}
                                        </span>
                                    )}
                                </OrderInfoElement>
                                <OrderInfoElement>
                                    Livraison{' '}
                                    {getDeliveryHourRangeString(
                                        order.deliveryTimeWindows?.[0]?.start,
                                        order.deliveryTimeWindows?.[0]?.stop,
                                    )}
                                </OrderInfoElement>
                                <OrderInfoElement>
                                    Mise en place des produits : {order.hasOnsiteSetup ? 'Oui ' : 'Non '}
                                    {order.hasOnsiteSetup && order.onsiteSetupInstructions && (
                                        <OrderOnsiteSetupInstructions
                                            data-for="onsiteSetupInstructionsTooltip"
                                            data-tip={order.onsiteSetupInstructions}
                                        >
                                            (voir vos instructions)
                                        </OrderOnsiteSetupInstructions>
                                    )}
                                    <FixedWidthTooltip
                                        id="onsiteSetupInstructionsTooltip"
                                        type="dark"
                                        effect="solid"
                                        place="bottom"
                                    />
                                </OrderInfoElement>
                                <OrderInfoElement>
                                    Fréquence:{' '}
                                    {order.isPunctual ? 'Ponctuelle' : getFrequencyLabel(order.frequency ?? '')}
                                </OrderInfoElement>
                            </OrderInfo>
                        </OrderInfoSection>
                        <PriceDetailSection>
                            {allProductsPrice !== allOrderedProductsPrice && (
                                <>
                                    <PriceLine>
                                        Total produits commandés: <Price>{formatPrice(allOrderedProductsPrice)}</Price>
                                    </PriceLine>
                                    <PriceLine isNegative>
                                        Total produits non livrés:{' '}
                                        {formatPrice(-(allOrderedProductsPrice - allProductsPrice))}
                                    </PriceLine>
                                </>
                            )}
                            <PriceLine>
                                Sous-total HT: <Price>{formatPrice(allProductsPrice)}</Price>
                            </PriceLine>
                            <PriceLine>{`Frais de livraison : ${formatPrice(order?.prices?.priceDelivery ?? 0)}`}</PriceLine>
                            {discountPrice > 0 && (
                                <PriceLine>
                                    {'Promo "' +
                                        discountDescription +
                                        ' ' +
                                        formattedDiscountEffect +
                                        '" : ' +
                                        formattedDiscountPrice}
                                </PriceLine>
                            )}
                            <PriceTotal>
                                Total HT :{' '}
                                {discountPrice > 0 && (
                                    <InitialPrice>{formatPrice(allProductPriceWithDelivery)}</InitialPrice>
                                )}
                                <Price hasDiscount={discountPrice > 0}>{formatPrice(totalPriceAfterDiscount)}</Price>
                            </PriceTotal>
                        </PriceDetailSection>
                    </OrderDetailsHeader>
                    <OrderDetailsProducts products={productsToShow} readOnly />
                </OrderDetailsContent>
            </ScrollableSection>
        </Container>
    );
};

const FixedWidthTooltip = styled(Tooltip)`
    max-width: 300px;
`;

const OrderOnsiteSetupInstructions = styled.span`
    text-decoration: underline;
`;

const OrderInfo = styled.ul`
    padding-inline-start: 17px;
`;

const OrderInfoElement = styled.li`
    margin-top: 10px;
`;

const OrderInfoSection = styled.div`
    display: flex;
    flex-direction: column;
`;

type PriceLineProps = {
    isNegative?: boolean;
    hasDiscount?: boolean;
};
const Price = styled.div<PriceLineProps>`
    color: ${({ theme, hasDiscount }) => hasDiscount && theme.colors.pink};
    margin-left: 5px;
`;

const InitialPrice = styled(Price)`
    color: ${({ theme }) => theme.colors.pantinGrey};
    text-decoration: line-through;
    text-decoration-color: ${({ theme }) => theme.colors.pantinGrey};
`;

const OrderTitle = styled.h3`
    margin: 15px 0 0 0;
    padding: 0;
`;

const PriceTotal = styled.h3`
    display: flex;
    margin: 15px 0 0 0;
    padding: 0;
`;

const PriceLine = styled.div<PriceLineProps>`
    display: flex;
    margin: 5px 0 0 0;
    padding: 0;
    color: ${({ theme, isNegative }) => isNegative && theme.colors.red};
    &::before {
        content: '(';
    }
    &::after {
        content: ')';
    }
`;

const PriceDetailSection = styled.section`
    margin-top: 5px;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    background-color: white;
    position: relative;
    flex-shrink: 1;
    width: 100%;
    height: 100%;
`;

const ScrollableSection = styled.div`
    overflow-y: auto;
    display: flex;
    flex-flow: column;
`;

const PageHeader = styled.header`
    display: flex;
    width: 100%;
    justify-content: space-between;
    padding: 1em 1.5em;
    background: ${({ theme }) => theme.colors.green};
`;

const PageTitleSection = styled.div`
    padding: 25px;
    display: flex;
    align-items: center;
    background: ${({ theme }) => theme.colors.culturedGrey};
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderGrey};
`;

const PageTitle = styled.h2`
    margin: 0;
    padding: 0 0 0 10px;
`;

const OrderDetailsContent = styled.div``;

const OrderDetailsHeader = styled.div`
    display: flex;
    justify-content: space-between;
    background: ${({ theme }) => theme.colors.culturedGrey};
    padding: 20px 55px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderGrey};
    @media (max-width: 800px) {
        flex-direction: column;
    }
`;
